import {
  call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import findResult, { createOrderByParams, hasMinimumLength, wildcardFilter } from '@/utils/resultsHelper';
import selectors from '@/selectors';
import callAdapter from '@/utils/sagaHelper';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import { states } from '@/constants/lifecycle';
import { claimStateWithPermissions } from '../state/state';
import RequestTasksService from '../service';
import {
  completeRequestTasksSuccess,
  showActiveRequestTask,
  showActiveRequestTaskError,
  showManageRequestTaskError,
  showManageRequestTaskSuccess,
  showTasks,
  showTasksError,
  showTaskViews,
  showTaskViewsError,
  showUpdateRequestTaskError,
  showUpdateRequestTaskSuccess,
} from '../actions';
import actions from '../constants';

export function* tasks(action) {
  const items = yield select(selectors.requestTasks);
  const {
    currentPage,
    display,
    filter,
    orderBy,
    pageSize,
    view,
  } = items[action.category];
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const orderByParams = createOrderByParams(orderBy);
  const { error: viewError } = hasMinimumLength(view)
    ? yield call(callAdapter, RequestTasksService.view(view))
    : { error: null };
  const { response, error } = yield call(callAdapter, RequestTasksService.tasks(
    wildcardFilter(filter),
    viewError ? null : view,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    orderByParams,
  ));

  if (error) {
    yield put(showTasksError(action.category, error));
  } else {
    yield put(showTasks(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
      viewError ? null : view,
    ));
  }
}

export function* views(action) {
  const items = yield select(selectors.requestTaskViews);
  const {
    currentPage,
    display,
    filter,
    orderBy,
    pageSize,
  } = items[action.category];
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const orderByParams = createOrderByParams(orderBy);
  const { response, error } = yield call(callAdapter, RequestTasksService.views(
    wildcardFilter(filter),
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    orderByParams,
  ));

  if (error) {
    yield put(showTaskViewsError(action.category, error));
  } else {
    yield put(showTaskViews(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* refreshTasksPresentation(action) {
  yield call(tasks, { ...action, page: 1, append: false });
}

function* activeTaskRequest(task) {
  const { response, error } = yield call(callAdapter, RequestTasksService.request(task.code));
  if (error) {
    yield put(showActiveRequestTaskError(task.code, error));
  } else {
    yield put(showActiveRequestTask(task, response));
  }
}

export function* activeTask() {
  const { code } = yield select(selectors.activeRequestTask);
  const { response, error } = yield call(callAdapter, RequestTasksService.task(code));
  if (error) {
    yield put(showActiveRequestTaskError(code, error));
  } else {
    yield activeTaskRequest(response);
  }
}

function* claimTask(item, silent = false, category = null) {
  const { response, error } = yield call(callAdapter, RequestTasksService.claim(item));
  if (error) {
    yield put(showUpdateRequestTaskError(category, item.code, item.name, error, silent));
  } else {
    yield put(showUpdateRequestTaskSuccess(item.code, item.name, response, silent));
  }
}

export function* claim({ category, code, silent }) {
  const openTask = yield select(selectors.activeRequestTask);
  if (openTask.open) {
    yield claimTask(openTask, true);
  } else {
    const items = yield select(selectors.requestTasks);
    const item = findResult(items, category, code);
    yield claimTask(item, silent, category);
  }
}

function* update(item) {
  const { response, error } = yield call(callAdapter, RequestTasksService.update(item));
  if (error) {
    yield put(showUpdateRequestTaskError(null, item.code, item.name, error, true));
  } else if (item.completeStatus === states.PENDING) {
    yield put(completeRequestTasksSuccess([response]));
  } else {
    yield put(showUpdateRequestTaskSuccess(item.code, item.name, response, false));
  }
}

function* claimAndUpdate(item) {
  const { error } = yield call(callAdapter, RequestTasksService.claim(item));
  if (error) {
    yield put(showActiveRequestTaskError(item.code, error));
  } else {
    yield update(item);
  }
}

export function* checkClaimAndUpdate() {
  const item = yield select(selectors.activeRequestTask);
  yield claimStateWithPermissions(item).claimed ? update(item) : claimAndUpdate(item);
}

export function* manage() {
  const item = yield select(selectors.manageRequestTask);
  const { response, error } = yield call(callAdapter, RequestTasksService.manage(item));
  if (error) {
    yield put(showManageRequestTaskError(error));
  } else {
    yield put(showManageRequestTaskSuccess(item.code, item.name, response));
  }
}

export default function* tasksApi() {
  yield takeEvery([
    actions.FETCH_REQUEST_TASKS,
    actions.REFRESH_REQUEST_TASKS,
    actions.TOGGLE_REQUEST_TASK_COLUMN_ORDER_BY,
    actions.UPDATE_REQUEST_TASKS_VIEW_FILTER,
  ], tasks);
  yield takeLatest(actions.FETCH_ACTIVE_REQUEST_TASK, activeTask);
  yield takeEvery(actions.FETCH_REQUEST_TASK_VIEWS, views);
  yield takeEvery(actions.TOGGLE_REQUEST_TASKS_PRESENTATION, refreshTasksPresentation);
  yield takeEvery(actions.CLAIM_REQUEST_TASK, claim);
  yield takeLatest([actions.COMPLETE_REQUEST_TASK, actions.SAVE_REQUEST_TASK], checkClaimAndUpdate);
  yield takeLatest(actions.MANAGE_REQUEST_TASK, manage);
}
